import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DeviceMasterService } from '../services/device-master.service';
import { Subscription } from 'rxjs';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { Router, ActivatedRoute, ParamMap, NavigationStart } from '@angular/router';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

interface SensorStatus {
  name: string;
  status: 'ok' | 'fail' | 'OFFLINEs';
  icon: string;
  message: string;
  failValue?: string;
  checking?: boolean;
  statusIcon?: string;
  statusClass?: string;
}

interface DeviceDiagnostic {
  deviceId: string;
  sensorStatus: SensorStatus[];
  visibleSensors: SensorStatus[];
  failedCount: number;
  workingCount: number;
  expanded: boolean;
  siteName?: string;
}

@Component({
  selector: 'app-device-diagostic',
  templateUrl: './device-diagostic.component.html',
  styleUrls: ['./device-diagostic.component.scss']
})
export class DeviceDiagosticComponent implements OnInit, OnDestroy {
  loading = true;
  devices: DeviceDiagnostic[] = [];
  filteredDevices: DeviceDiagnostic[] = [];
  selectedDevice: DeviceDiagnostic | null = null;
  scanning = false;
  progress = 0;
  deviceIds: string[] = [];
  sub: Subscription;
  scanIntervalId: any;
  showSummary = true;
  searchText = '';
  activeFilter: string = 'all'; 
  showInitialAnimation = true;
  animationProgress = 0;
  animationMessage = 'Initializing diagnostic system...';
  selectedSensorType: string = '';

  constructor(
    private router: Router,
    private deviceManagerSer: DeviceMasterService
  ) { }

  ngOnInit(): void {

    if (this.deviceManagerSer.details) {
      this.deviceIds = this.deviceManagerSer.details;
    } else {
        this.deviceIds = [];
    }

    this.startInitialAnimation();
  }



  ngOnDestroy(): void {
    if (this.scanIntervalId) {
      clearInterval(this.scanIntervalId);
    }
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  // Initial animation sequence
  startInitialAnimation(): void {
    const self = this;
    const animationSteps = [
      { progress: 15, message: 'Connecting to devices...', delay: 2600 },
      { progress: 35, message: 'Scanning sensor networks...', delay: 2700 },
      { progress: 55, message: 'Reading sensor data...', delay: 2900 },
      { progress: 75, message: 'Analyzing diagnostics...', delay: 2800 },
      { progress: 90, message: 'Generating report...', delay: 2900 },
      { progress: 100, message: 'Complete!', delay: 1900 }
    ];

    let currentStep = 0;

    const processStep = function () {
      if (currentStep < animationSteps.length) {
        const step = animationSteps[currentStep];
        self.animationProgress = step.progress;
        self.animationMessage = step.message;
        currentStep++;

        setTimeout(function () {
          processStep();
        }, step.delay);
      } else {
        setTimeout(function () {
          self.showInitialAnimation = false;
          self.fetchLatestData();
        }, 900);
      }
    };

    processStep();
  }

  fetchLatestData(): void {
    this.loading = true;
    const onlyIds = this.deviceIds.map(x => x.split(" - ")[0]);
    this.sub = this.deviceManagerSer.getLastdatatransformer({ deviceid: onlyIds })
      .subscribe(
        (resp: any) => {
          console.log("Diagnostic Data Response:", resp);
          if (resp && resp.code === '1' && resp.result && resp.result.length > 0) {
            // Process each device result
            const deviceSiteMap: any = {};

            this.deviceIds.forEach(d => {
              const [id, site] = d.split(" - ");
              deviceSiteMap[id] = site || '';
            });
            this.devices = resp.result.map((record: any) => {
              const id = record.Deviceid || record.deviceId;
              const readings = this.parseReadings(record.readings);
              const sensorStatus = this.evaluateSensorFailures(record, readings);

              return {
                deviceId: id || 'Unknown',
                siteName: deviceSiteMap[id] || '',
                sensorStatus: sensorStatus,
                visibleSensors: [],
                failedCount: sensorStatus.filter(function (s) { return s.status === 'fail'; }).length,
                workingCount: sensorStatus.filter(function (s) { return s.status === 'ok'; }).length,
                offlineCount: sensorStatus.filter(function (s) { return s.status === 'OFFLINEs'; }).length,
                expanded: false
              };
            });

            // Sort devices by failed count (descending)
            this.devices.sort(function (a, b) { return b.failedCount - a.failedCount; });
            this.filteredDevices = this.devices.slice();

          } else {
            this.devices = [{
              deviceId: 'No Data',
              sensorStatus: [{
                name: 'No Data Available',
                status: 'fail',
                icon: 'error',
                message: 'No transformer data found.'
              }],
              visibleSensors: [],
              failedCount: 1,
              workingCount: 0,
              expanded: false
            }];
            this.filteredDevices = this.devices.slice();
          }

          this.loading = false;
        },
        (err) => {
          console.error("Error fetching diagnostic data:", err);
          this.devices = [{
            deviceId: 'Error',
            sensorStatus: [{
              name: 'Error',
              status: 'fail',
              icon: 'error',
              message: 'Could not load diagnostic data.'
            }],
            visibleSensors: [],
            failedCount: 1,
            workingCount: 0,
            expanded: false
          }];
          this.filteredDevices = this.devices.slice();
          this.loading = false;
        }
      );
  }

  // Search functionality
  onSearchChange(): void {
    const search = this.searchText.toLowerCase().trim();

    if (!search) {
      this.filteredDevices = this.devices.slice();
    } else {
      this.filteredDevices = this.devices.filter(function (device) {
        return device.deviceId.toLowerCase().includes(search);
      });
    }

    // Apply active filter after search
    this.applyActiveFilter();
  }

  clearSearch(): void {
    this.searchText = '';
    this.activeFilter = 'all';
    this.filteredDevices = this.devices.slice();
  }

  // Filter by device status
  filterByStatus(status: string): void {
    this.activeFilter = status;
    this.applyActiveFilter();
  }

  // Filter by sensor type
  filterBySensorType(sensorName: string): void {
    this.activeFilter = sensorName;
    this.selectedSensorType = sensorName;
    this.applyActiveFilter();
  }

  // Get devices affected by specific sensor
  getDevicesBySensorType(sensorName: string): DeviceDiagnostic[] {
    return this.devices.filter(function (d) {
      return d.sensorStatus.some(function (s) {
        return s.name === sensorName && s.status === 'fail';
      });
    });
  }

  // Close sensor details modal
  closeSensorDetails(): void {
    this.selectedSensorType = '';
  }

  // Apply the current active filter
  applyActiveFilter(): void {
    let baseDevices = this.devices.slice();

    // First apply search filter if exists
    if (this.searchText) {
      const search = this.searchText.toLowerCase().trim();
      baseDevices = baseDevices.filter(function (device) {
        return device.deviceId.toLowerCase().includes(search);
      });
    }

    // Then apply status/sensor filter
    if (this.activeFilter === 'all') {
      this.filteredDevices = baseDevices;
    } else if (this.activeFilter === 'healthy') {
      this.filteredDevices = baseDevices.filter(function (d) {
        return d.failedCount === 0;
      });
    } else if (this.activeFilter === 'issues') {
      this.filteredDevices = baseDevices.filter(function (d) {
        return d.failedCount > 0;
      });
    } else if (this.activeFilter === 'offlline') {
      this.filteredDevices = baseDevices.filter(function (d: any) {
        return d.offlineCount > 0;
      });
    } else {
      // Filter by specific sensor type
      const sensorName = this.activeFilter;
      this.filteredDevices = baseDevices.filter(function (d) {
        return d.sensorStatus.some(function (s) {
          return s.name === sensorName && s.status === 'fail';
        });
      });
    }
  }

  // Clear all filters
  clearAllFilters(): void {
    this.activeFilter = 'all';
    this.searchText = '';
    this.filteredDevices = this.devices.slice();
  }

  parseReadings(readingsStr: any): any {
    try {
      return readingsStr ? JSON.parse(readingsStr) : {};
    } catch (e) {
      console.error("Error parsing readings:", e);
      return {};
    }
  }

  viewDeviceDetails(device: DeviceDiagnostic): void {
    this.selectedDevice = device;
    this.showSummary = false;
    this.startSequentialScan(device);
  }

  backToSummary(): void {
    this.showSummary = true;
    this.selectedDevice = null;
    this.scanning = false;
  }

  startSequentialScan(device: DeviceDiagnostic, speed: number = 1500): void {
    device.visibleSensors = [];
    this.scanning = true;
    this.progress = 0;

    let index = 0;
    const total = device.sensorStatus.length;
    const self = this;

    const processNextSensor = function () {
      if (index < total) {
        const sensor = device.sensorStatus[index];

        const checkingObj: SensorStatus = {
          name: sensor.name,
          icon: sensor.icon,
          message: 'Checking...',
          status: sensor.status,
          failValue: sensor.failValue,
          checking: true,
          statusIcon: '',
          statusClass: ''
        };

        device.visibleSensors.push(checkingObj);
        const currentIndex = index;

        setTimeout(function () {
          const finalObj: SensorStatus = {
            name: sensor.name,
            icon: sensor.icon,
            message: sensor.message,
            failValue: sensor.failValue,
            status: sensor.status,
            checking: false,
            statusIcon: sensor.status === 'ok' ? 'check_circle' : 'cancel',
            statusClass: sensor.status === 'ok' ? 'ok' : 'fail'
          };
          device.visibleSensors[currentIndex] = finalObj;
          self.progress = Math.round(((currentIndex + 1) / total) * 100);
          index++;
          setTimeout(function () {
            processNextSensor();
          }, 300);

        }, speed);

      } else {
        self.scanning = false;
      }
    };

    processNextSensor();
  }

  getSensorClass(index: number): string {
    if (!this.selectedDevice) return 'pending';

    if (index < this.selectedDevice.visibleSensors.length) {
      const sensor = this.selectedDevice.visibleSensors[index];
      if (sensor.checking) {
        return 'checking';
      } else {
        return sensor.statusClass || 'pending';
      }
    }
    return 'pending';
  }

  isChecking(index: number): boolean {
    return this.selectedDevice !== null &&
      index < this.selectedDevice.visibleSensors.length &&
      this.selectedDevice.visibleSensors[index].checking === true;
  }

  isCompleted(index: number): boolean {
    return this.selectedDevice !== null &&
      index < this.selectedDevice.visibleSensors.length &&
      !this.selectedDevice.visibleSensors[index].checking;
  }

  isPending(index: number): boolean {
    return this.selectedDevice === null || index >= this.selectedDevice.visibleSensors.length;
  }

  getCompletedSensor(index: number): SensorStatus | null {
    if (this.selectedDevice && index < this.selectedDevice.visibleSensors.length) {
      return this.selectedDevice.visibleSensors[index];
    }
    return null;
  }

  evaluateSensorFailures(raw: any, readings: any): SensorStatus[] {
    if (raw.device_status === 'OFFLINE') {
      return [{
        name: 'OFFLINE',
        status: 'OFFLINEs',
        icon: 'warning_amber',
        message: 'Device is offline. No recent data received.'
      }];
    }
    const list: SensorStatus[] = [];

    function isZero(v: any): boolean {
      return v === "000";
    }

    function check_lug(v: any): boolean {
      const val = parseFloat(v);
      return val <= 12 || val >= 400;
    }

    // SHT Sensor (Temperature & Humidity)
    if (isZero(raw.Ambient_Temperature) || isZero(raw.Humidity)) {
      list.push({
        name: 'SHT Sensor',
        status: 'fail',
        icon: 'device_thermostat',
        message: 'Temperature / humidity reading invalid.',
        failValue: 'Temp: ' + raw.Ambient_Temperature + ', Humidity: ' + raw.Humidity
      });
    } else {
      list.push({
        name: 'SHT Sensor',
        status: 'ok',
        icon: 'device_thermostat',
        message: 'Normal readings.'
      });
    }
    const lugEnabled = Number(raw.has_lug) === 1;
    // LUG Temperature Sensor
    if (lugEnabled) {
      const rlug = readings["Lug_Temp_R"];
      const ylug = readings["Lug_Temp_Y"];
      const blug = readings["Lug_Temp_B"];

      if (check_lug(rlug) || check_lug(ylug) || check_lug(blug)) {
        list.push({
          name: 'LUG Temperature Sensor',
          status: 'fail',
          icon: 'warning_amber',
          message: 'LUG Temperature Sensor Failure.',
          failValue: 'R: ' + rlug + '°C, Y: ' + ylug + '°C, B: ' + blug + '°C'
        });
      } else {
        list.push({
          name: 'LUG Temperature Sensor',
          status: 'ok',
          icon: 'warning_amber',
          message: 'Healthy.'
        });
      }
    } else {
      // SENSOR NOT PRESENT
      list.push({
        name: 'LUG Temperature Sensor',
        status: 'ok',
        icon: 'info',
        message: 'LUG temperature sensor not installed.'
      });
    }
     const multifunctionenabled = Number(raw.ele) === 1;
    if (multifunctionenabled) {
    // Multifunction Meter
    const rpn = readings["R_Ph_N"];
    const kwh = readings["System_kwh"];

    if (isZero(rpn) || isZero(kwh)) {
      list.push({
        name: 'Multifunction Meter',
        status: 'fail',
        icon: 'bolt',
        message: 'Voltage or energy reading invalid.',
        failValue: 'R_Ph_N: ' + rpn + ', kWh: ' + kwh
      });
    } else {
      list.push({
        name: 'Multifunction Meter',
        status: 'ok',
        icon: 'bolt',
        message: 'Healthy readings.'
      });
    }
  }else{
 list.push({
        name: 'Multifunction Meter',
        status: 'ok',
        icon: 'info',
        message: 'Multifunction Meter not Given.'
      });
  }
    // Moisture Sensor
    if (isZero(raw.Water_Activity_aw)) {
      list.push({
        name: 'Moisture Sensor',
        status: 'fail',
        icon: 'opacity',
        message: 'Invalid moisture reading.',
        failValue: 'Water Activity: ' + raw.Water_Activity_aw
      });
    } else {
      list.push({
        name: 'Moisture Sensor',
        status: 'ok',
        icon: 'opacity',
        message: 'Moisture normal.'
      });
    }

    // Oil Temperature Sensor has_oil_temp
    const oiltempenabled = Number(raw.has_oil_temp) === 1;
    if (oiltempenabled) {
      const oilTemp = parseFloat(raw.Oil_Temperature);
      if (oilTemp <= 12 || oilTemp >= 149) {
        list.push({
          name: 'Oil Temperature Sensor',
          status: 'fail',
          icon: 'hot_tub',
          message: 'Oil temperature out of safe range.',
          failValue: raw.Oil_Temperature + ' °C'
        });
      } else {
        list.push({
          name: 'Oil Temperature Sensor',
          status: 'ok',
          icon: 'hot_tub',
          message: 'Oil temperature normal (' + oilTemp + '°C)'
        });
      }
    } else {
      // SENSOR NOT PRESENT
      list.push({
        name: 'Oil Temperature Sensor',
        status: 'ok',
        icon: 'info',
        message: 'Oil Temperature Sensor not installed.'
      });
    }
    return list;
  }


  getWorkingCount(): number {
    if (this.selectedDevice) {
      return this.selectedDevice.visibleSensors.filter(function (s) {
        return s.statusClass === 'ok';
      }).length;
    }
    return 0;
  }

  getTotalFailedDevices(): number {
    return this.devices.filter(d => d.failedCount > 0).length;
  }

  getTotal(): number {
    return this.devices.length;
  }

  getTotalWorkingDevices(): number {
    return this.devices.filter((d: any) =>
      d.offlineCount === 0 && d.failedCount === 0
    ).length;
  }

  getofflineWorkingDevices(): number {
    return this.devices.filter((d: any) =>
      d.offlineCount > 0
    ).length;
  }


  getFailedSensorsByType(): { [key: string]: number } {
    const summary: { [key: string]: number } = {};
    const self = this;

    // Always calculate from all devices, not filtered
    this.devices.forEach(function (device) {
      device.sensorStatus.forEach(function (sensor) {
        if (sensor.status === 'fail') {
          if (summary[sensor.name]) {
            summary[sensor.name]++;
          } else {
            summary[sensor.name] = 1;
          }
        }
      });
    });

    return summary;
  }

  onSensorClick(sensor: SensorStatus): void {
    if (sensor.status === 'fail' && sensor.failValue) {
      console.log('Sensor failure details:', sensor);
    }
  }

  // Generate PDF Report using pdfMake
  downloadPDFReport(): void {
    const currentDate = new Date();
    const dateStr = currentDate.toLocaleDateString();
    const timeStr = currentDate.toLocaleTimeString();
    const self = this;

    // Prepare table data for devices with issues
    const deviceTableBody: any[] = [
      [
        { text: 'Device ID', style: 'tableHeader', fillColor: '#667eea', color: '#ffffff' },
        { text: 'Failed Sensors', style: 'tableHeader', fillColor: '#667eea', color: '#ffffff' },
        { text: 'Working Sensors', style: 'tableHeader', fillColor: '#667eea', color: '#ffffff' },
        { text: 'Status', style: 'tableHeader', fillColor: '#667eea', color: '#ffffff' }
      ]
    ];
    this.filteredDevices.forEach(function (device) {
      const statusColor = device.failedCount > 0 ? '#ef4444' : '#10b981';
      const statusText = device.failedCount > 0 ? 'Issues Found' : 'Healthy';

      deviceTableBody.push([
        { text: `${device.deviceId} - ${device.siteName}`, style: 'tableCell' },
        { text: device.failedCount.toString(), style: 'tableCell', color: '#ef4444' },
        { text: device.workingCount.toString(), style: 'tableCell', color: '#10b981' },
        { text: statusText, style: 'tableCell', color: statusColor, bold: true }
      ]);
    });

    // Prepare detailed failure information
    const failureDetails: any[] = [];

    this.filteredDevices.forEach(function (device) {
      const failedSensors = device.sensorStatus.filter(function (s) {
        return s.status === 'fail';
      });

      if (failedSensors.length > 0) {
        failureDetails.push({
          text: 'Device: ' + device.deviceId + " - " + device.siteName,
          style: 'deviceHeader',
          margin: [0, 15, 0, 10]
        });

        const sensorTableBody: any[] = [
          [
            { text: 'Sensor Name', style: 'subTableHeader', fillColor: '#fee2e2' },
            { text: 'Status', style: 'subTableHeader', fillColor: '#fee2e2' },
            { text: 'Message', style: 'subTableHeader', fillColor: '#fee2e2' }
          ]
        ];

        failedSensors.forEach(function (sensor) {
          sensorTableBody.push([
            { text: sensor.name, style: 'tableCell' },
            { text: 'FAILED', style: 'tableCell', color: '#ef4444', bold: true },
            { text: sensor.message, style: 'tableCell', fontSize: 9 }
          ]);

          if (sensor.failValue) {
            sensorTableBody.push([
              { text: 'Failure Values:', colSpan: 3, style: 'failValue', margin: [5, 2, 0, 2] },
              {},
              {}
            ]);
            sensorTableBody.push([
              { text: sensor.failValue, colSpan: 3, style: 'failValueData', margin: [10, 2, 0, 5] },
              {},
              {}
            ]);
          }
        });

        failureDetails.push({
          table: {
            headerRows: 1,
            widths: ['30%', '15%', '55%'],
            body: sensorTableBody
          },
          layout: {
            fillColor: function (rowIndex: number) {
              return (rowIndex === 0) ? '#fee2e2' : ((rowIndex % 2 === 0) ? '#fef2f2' : null);
            }
          }
        });
      }
    });

    // Prepare sensor type summary
    const failuresBySensor = this.getFailedSensorsByType();
    const sensorSummaryBody: any[] = [
      [
        { text: 'Sensor Type', style: 'tableHeader', fillColor: '#667eea', color: '#ffffff' },
        { text: 'Affected Devices', style: 'tableHeader', fillColor: '#667eea', color: '#ffffff' }
      ]
    ];

    const sensorKeys = Object.keys(failuresBySensor);
    for (let i = 0; i < sensorKeys.length; i++) {
      const sensorName = sensorKeys[i];
      const count = failuresBySensor[sensorName];
      sensorSummaryBody.push([
        { text: sensorName, style: 'tableCell' },
        { text: count.toString(), style: 'tableCell', color: '#ef4444', bold: true }
      ]);
    }

    // Build content array
    const contentArray: any[] = [];

    // Summary Section
    contentArray.push({
      text: 'Executive Summary',
      style: 'sectionHeader'
    });

    contentArray.push({
      columns: [
        {
          width: '33%',
          stack: [
            { text: self.filteredDevices.length.toString(), style: 'summaryValue', color: '#667eea' },
            { text: 'Total Devices', style: 'summaryLabel' }
          ],
          margin: [0, 10, 0, 20]
        },
        {
          width: '33%',
          stack: [
            { text: self.getTotalWorkingDevices().toString(), style: 'summaryValue', color: '#10b981' },
            { text: 'Healthy Devices', style: 'summaryLabel' }
          ],
          margin: [0, 10, 0, 20]
        },
        {
          width: '34%',
          stack: [
            { text: self.getTotalFailedDevices().toString(), style: 'summaryValue', color: '#ef4444' },
            { text: 'Devices with Issues', style: 'summaryLabel' }
          ],
          margin: [0, 10, 0, 20]
        }
      ]
    });

    // Device Overview Table
    contentArray.push({
      text: 'Device Overview',
      style: 'sectionHeader',
      margin: [0, 20, 0, 10]
    });

    contentArray.push({
      table: {
        headerRows: 1,
        widths: ['30%', '20%', '25%', '25%'],
        body: deviceTableBody
      },
      layout: {
        fillColor: function (rowIndex: number) {
          return (rowIndex === 0) ? '#667eea' : ((rowIndex % 2 === 0) ? '#f8fafc' : null);
        }
      }
    });

    // Sensor Type Summary (if exists)
    if (sensorKeys.length > 0) {
      contentArray.push({
        text: 'Failures by Sensor Type',
        style: 'sectionHeader',
        margin: [0, 20, 0, 10],
        pageBreak: 'before'
      });

      contentArray.push({
        table: {
          headerRows: 1,
          widths: ['70%', '30%'],
          body: sensorSummaryBody
        },
        layout: {
          fillColor: function (rowIndex: number) {
            return (rowIndex === 0) ? '#667eea' : ((rowIndex % 2 === 0) ? '#f8fafc' : null);
          }
        }
      });
    }

    // Detailed Failures
    if (failureDetails.length > 0) {
      contentArray.push({
        text: 'Detailed Sensor Failures',
        style: 'sectionHeader',
        margin: [0, 20, 0, 10],
        pageBreak: 'before'
      });

      for (let i = 0; i < failureDetails.length; i++) {
        contentArray.push(failureDetails[i]);
      }
    }

    // Define PDF document structure
    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [40, 80, 40, 60],
      header: function (currentPage: number, pageCount: number) {
        return {
          columns: [
            {
              text: 'SENSOR DIAGNOSTIC REPORT',
              style: 'reportHeader',
              margin: [40, 30, 0, 0]
            },
            {
              text: 'Page ' + currentPage + ' of ' + pageCount,
              alignment: 'right',
              style: 'pageNumber',
              margin: [0, 30, 40, 0]
            }
          ]
        };
      },
      footer: function (currentPage: number, pageCount: number) {
        return {
          columns: [
            {
              text: 'Generated: ' + dateStr + ' ' + timeStr,
              style: 'footer',
              margin: [40, 20, 0, 0]
            },
            {
              text: 'Industrial IOT Platform',
              alignment: 'right',
              style: 'footer',
              margin: [0, 20, 40, 0]
            }
          ]
        };
      },
      content: contentArray,
      styles: {
        reportHeader: {
          fontSize: 18,
          bold: true,
          color: '#667eea'
        },
        pageNumber: {
          fontSize: 10,
          color: '#64748b'
        },
        footer: {
          fontSize: 9,
          color: '#94a3b8'
        },
        sectionHeader: {
          fontSize: 16,
          bold: true,
          color: '#1e293b',
          margin: [0, 15, 0, 10]
        },
        deviceHeader: {
          fontSize: 14,
          bold: true,
          color: '#334155'
        },
        summaryValue: {
          fontSize: 32,
          bold: true,
          alignment: 'center'
        },
        summaryLabel: {
          fontSize: 11,
          alignment: 'center',
          color: '#64748b',
          margin: [0, 5, 0, 0]
        },
        tableHeader: {
          fontSize: 11,
          bold: true,
          alignment: 'center',
          margin: [0, 5, 0, 5]
        },
        subTableHeader: {
          fontSize: 10,
          bold: true,
          color: '#991b1b',
          margin: [0, 5, 0, 5]
        },
        tableCell: {
          fontSize: 10,
          margin: [0, 5, 0, 5]
        },
        failValue: {
          fontSize: 9,
          bold: true,
          color: '#dc2626',
          italics: true
        },
        failValueData: {
          fontSize: 8,
          color: '#991b1b'
        }
      },
      defaultStyle: {
        font: 'Roboto'
      }
    };

    // Generate and download PDF
    const fileName = 'Sensor-Diagnostic-Report-' + currentDate.getTime() + '.pdf';
    pdfMake.createPdf(docDefinition).download(fileName);
  }

}